import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import ImageWithFallback from './ImageWithFallback';
import { useMatches } from '../hooks/useSupabase';
import { useSite } from '../contexts/SiteContext';
import {
  formatMatchDate,
  formatMatchTime,
  getMatchPhase,
  isAoxTeam,
  matchDateTime,
  normalizeMatchdaySettings,
  selectMatchdayMatches,
} from '../lib/matchday';
import * as FiIcons from 'react-icons/fi';

const { FiClock, FiMapPin, FiRadio, FiArrowRight, FiCalendar, FiZap, FiChevronRight } = FiIcons;

const initials = (name = '') => name.split(/\s+/).filter(Boolean).slice(0, 3).map((part) => part[0]).join('').toUpperCase();

const TeamMark = ({ name, logo, isAox, siteLogo }) => (
  <div className="flex min-w-0 flex-col items-center">
    <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border border-white/25 bg-white/10 shadow-2xl backdrop-blur-sm md:h-28 md:w-28">
      {logo || isAox ? (
        <ImageWithFallback src={logo || siteLogo} alt={name} className="h-full w-full object-contain p-2" />
      ) : (
        <span className="text-xl font-black text-white md:text-2xl">{initials(name)}</span>
      )}
    </div>
    <div className="mt-3 max-w-[170px] text-center text-base font-black leading-tight text-white md:mt-4 md:max-w-[250px] md:text-2xl">{name}</div>
  </div>
);

const Countdown = ({ kickoff }) => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const diff = Math.max(0, kickoff.getTime() - now);
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return (
    <div className="mx-auto mt-5 grid max-w-xl grid-cols-4 gap-2 md:gap-3">
      {[[days, 'ΗΜ'], [hours, 'ΩΡ'], [minutes, 'ΛΕΠ'], [seconds, 'ΔΕΥ']].map(([value, label]) => (
        <div key={label} className="rounded-xl border border-white/15 bg-slate-950/35 px-2 py-2.5 backdrop-blur-md md:rounded-2xl md:px-4 md:py-3">
          <div className="text-xl font-black tabular-nums text-white md:text-3xl">{String(value).padStart(2, '0')}</div>
          <div className="mt-1 text-[9px] tracking-[0.2em] text-blue-100 md:text-[11px]">{label}</div>
        </div>
      ))}
    </div>
  );
};

const FollowingMatch = ({ match }) => (
  <Link
    to={`/matches/${match.id}`}
    className="group flex items-center justify-between gap-4 rounded-xl border border-white/15 bg-slate-950/35 px-4 py-3 text-left backdrop-blur-md transition-colors hover:bg-white/10"
  >
    <div className="min-w-0">
      <div className="mb-1 text-[10px] font-black uppercase tracking-[0.18em] text-emerald-200">Ακολουθεί</div>
      <div className="truncate text-sm font-bold text-white md:text-base">{match.home_team} - {match.away_team}</div>
      <div className="mt-1 text-xs text-blue-100">
        {formatMatchDate(match)}{match.time ? ` • ${formatMatchTime(match)}` : ''}{match.team_category ? ` • ${match.team_category}` : ''}
      </div>
    </div>
    <SafeIcon icon={FiChevronRight} className="h-5 w-5 flex-shrink-0 text-emerald-200 transition-transform group-hover:translate-x-1" />
  </Link>
);

const PitchBackground = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
    <div
      className="absolute inset-0"
      style={{
        backgroundImage: [
          'linear-gradient(180deg, rgba(3,18,43,.86), rgba(3,27,35,.68) 35%, rgba(4,60,42,.74) 100%)',
          'repeating-linear-gradient(90deg, rgba(255,255,255,.035) 0, rgba(255,255,255,.035) 9%, rgba(0,0,0,.025) 9%, rgba(0,0,0,.025) 18%)',
          'radial-gradient(circle at 18% 0%, rgba(120,200,255,.20), transparent 28%)',
          'radial-gradient(circle at 82% 0%, rgba(120,200,255,.18), transparent 28%)',
        ].join(', '),
      }}
    />
    <div className="absolute inset-x-[7%] bottom-[-22%] top-[12%] rounded-[28px] border-2 border-white/15 shadow-[0_0_80px_rgba(46,204,113,0.08)_inset] md:inset-x-[12%]" />
    <div className="absolute bottom-[-22%] left-1/2 top-[12%] w-px -translate-x-1/2 bg-white/15" />
    <div className="absolute left-1/2 top-[53%] h-28 w-28 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white/15 md:h-36 md:w-36" />
    <div className="absolute left-1/2 top-[53%] h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/20" />
    <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-black/45 to-transparent" />
    <div className="absolute -left-20 top-[-100px] h-72 w-72 rotate-12 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,.13),transparent_65%)] blur-xl" />
    <div className="absolute -right-20 top-[-100px] h-72 w-72 -rotate-12 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,.12),transparent_65%)] blur-xl" />
  </div>
);

const MatchdayHero = () => {
  const { data: matches, loading } = useMatches();
  const { siteConfig } = useSite();
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  const settings = useMemo(() => normalizeMatchdaySettings(siteConfig), [siteConfig]);
  const selection = useMemo(() => selectMatchdayMatches(matches || [], now, settings), [matches, now, settings]);
  const match = selection.primary;
  const phase = match ? getMatchPhase(match, now, settings) : 'normal';
  const kickoff = match ? matchDateTime(match) : null;

  if (loading || !settings.enabled || !match || !['soon', 'matchday', 'live'].includes(phase)) return null;

  const live = phase === 'live';
  const today = phase === 'matchday';
  const homeIsAox = isAoxTeam(match.home_team);
  const awayIsAox = isAoxTeam(match.away_team);

  return (
    <motion.section
      initial={{ opacity: 0, y: -14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      className="relative isolate overflow-hidden border-b border-white/10 bg-[#061a34] text-white shadow-[0_22px_50px_rgba(3,18,43,.20)]"
      aria-label="Επόμενος αγώνας ΑΟ Χανιά"
    >
      <PitchBackground />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 py-7 sm:px-6 md:py-9 lg:px-8">
        <div className="mx-auto max-w-6xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-slate-950/35 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] backdrop-blur-md md:text-sm">
            <SafeIcon icon={live ? FiRadio : FiZap} className={`h-4 w-4 ${live ? 'animate-pulse text-red-300' : 'text-yellow-300'}`} />
            {live ? 'LIVE MATCH' : today ? 'MATCHDAY' : 'NEXT MATCH'}
          </div>

          <div className="mt-3 text-xs font-bold uppercase tracking-[0.16em] text-blue-100 md:text-sm">
            {match.tournament || 'Αγωνιστική Δραστηριότητα'}{match.round_label ? ` • ${match.round_label}` : ''}
          </div>

          <div className="mt-5 grid grid-cols-[1fr_auto_1fr] items-center gap-3 md:mt-6 md:gap-10">
            <TeamMark name={match.home_team} logo={match.home_logo_url} isAox={homeIsAox} siteLogo={siteConfig.logo_url} />
            <div className="text-center">
              {live ? (
                <>
                  <div className="mb-2 text-[10px] font-black tracking-[0.18em] text-red-200 md:text-xs">ΣΕ ΕΞΕΛΙΞΗ</div>
                  <div className="text-4xl font-black md:text-6xl">{match.home_score ?? 0}<span className="mx-2 text-white/40 md:mx-4">:</span>{match.away_score ?? 0}</div>
                </>
              ) : (
                <div className="text-2xl font-black text-white/80 md:text-4xl">VS</div>
              )}
            </div>
            <TeamMark name={match.away_team} logo={match.away_logo_url} isAox={awayIsAox} siteLogo={siteConfig.logo_url} />
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-blue-100 md:text-sm">
            <span className="inline-flex items-center"><SafeIcon icon={FiCalendar} className="mr-2 h-4 w-4" />{formatMatchDate(match)}</span>
            {match.time && <span className="inline-flex items-center"><SafeIcon icon={FiClock} className="mr-2 h-4 w-4" />{formatMatchTime(match)}</span>}
            {match.location && <span className="inline-flex items-center"><SafeIcon icon={FiMapPin} className="mr-2 h-4 w-4" />{match.location}</span>}
          </div>

          {!live && kickoff && <Countdown kickoff={kickoff} />}

          <div className="mt-5 flex flex-col justify-center gap-3 sm:flex-row">
            <Link to={`/matches/${match.id}`} className="inline-flex items-center justify-center rounded-xl bg-white px-6 py-3 font-black text-primary-800 shadow-xl transition-colors hover:bg-blue-50">
              Match Center <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" />
            </Link>
            {match.stream_url && (
              <a href={match.stream_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-xl bg-red-600 px-6 py-3 font-black shadow-xl transition-colors hover:bg-red-700">
                <SafeIcon icon={FiRadio} className="mr-2 h-4 w-4" /> Δείτε τη μετάδοση
              </a>
            )}
          </div>

          {selection.following.length > 0 && (
            <div className={`mx-auto mt-5 grid max-w-3xl gap-3 ${selection.following.length > 1 ? 'md:grid-cols-2' : ''}`}>
              {selection.following.map((nextMatch) => <FollowingMatch key={nextMatch.id} match={nextMatch} />)}
            </div>
          )}
        </div>
      </div>
    </motion.section>
  );
};

export default MatchdayHero;
